/**
 * Zugriff auf das eigene Backend.
 *
 * Die Verwaltung spricht ausschließlich mit `apps/admin-api` unter ihrem
 * eigenen Host (SECURITY.md §1). Die Sitzung steckt im `HttpOnly`-Cookie und
 * wird von `credentials: 'same-origin'` mitgeschickt, ohne dass JavaScript sie
 * je zu sehen bekommt.
 *
 * Bewusst eine eigene Datei und nicht die aus `apps/web`: Beide sind heute
 * fast gleich, aber sie gehören verschiedenen Oberflächen mit verschiedenen
 * Sitzungen. Eine gemeinsame Datei lüde dazu ein, den einen Sonderfall im
 * anderen mitzuerledigen.
 */

import type { Fehler } from '@nac2s/shared'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Nicht angemeldet — die Oberfläche zeigt dann das Anmeldeformular. */
  get istNichtAngemeldet(): boolean {
    return this.status === 401
  }
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as Partial<Fehler> & { message?: string }
    // Der Zweig `/verwaltung/auth` gehört Better Auth und antwortet im eigenen
    // Format (securitytodo.md ST-024) — beide Formen werden hier zusammengeführt.
    const message = body.error ?? body.message ?? 'Unbekannter Fehler'
    return new ApiError(response.status, body.code ?? 'unknown', message)
  } catch {
    return new ApiError(response.status, 'unknown', 'Der Dienst hat unerwartet geantwortet.')
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response
  try {
    response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...init.headers,
      },
    })
  } catch {
    throw new ApiError(0, 'unreachable', 'Der Dienst ist nicht erreichbar.')
  }

  if (!response.ok) throw await parseError(response)
  if (response.status === 204) return undefined as T

  return (await response.json()) as T
}
